import { useMemo } from 'react';
import { Coordinates } from '../../../types';
import { Crosswalk, LocationInput } from '@kinetic-ui/data-layer';
import { Crosswalk as CrosswalkComponent } from '../Crosswalk';
import { useSelectedCrosswalk } from '../useSelectedCrosswalk';
import { RecursivePartial } from 'types/util';
import { MapSVGOverlay } from 'components/locations/MapSVGOverlay';
import { DragElement } from 'components/maps/DraggableMapElements';

export interface EditMapCrosswalksProps {
  location: RecursivePartial<LocationInput>;
  onClickCrosswalk?: (event?: React.MouseEvent) => void;
}

export const EditMapCrosswalks: React.FunctionComponent<EditMapCrosswalksProps> = ({
  location,
}) => {
  const { selectedCrosswalk, setSelectedCrosswalk } = useSelectedCrosswalk();

  const otherCrosswalks = useMemo(() => {
    if (!location?.crosswalks) {
      return [];
    }
    return selectedCrosswalk
      ? location.crosswalks.filter((c) => {
          return c.id !== selectedCrosswalk.id;
        })
      : location.crosswalks;
  }, [selectedCrosswalk, location.crosswalks]);

  const handlePointUpdate = (lngLat: [number, number], index: number) => {
    const newCoordinates = selectedCrosswalk?.geometry.coordinates.map(
      (coords: Coordinates, coordIndex: number) => {
        if (index === coordIndex) {
          return lngLat;
        }
        return coords;
      }
    );
    setSelectedCrosswalk((prev) => ({
      ...prev,
      geometry: { type: 'LineString', coordinates: newCoordinates },
    }));
  };

  const handlePointDeletion = (index: number) => {
    const newCoordinates = selectedCrosswalk?.geometry.coordinates.filter(
      (_c: Coordinates, coordIndex: number) => {
        return index !== coordIndex;
      }
    );
    setSelectedCrosswalk((prev) => ({
      ...prev,
      geometry: { type: 'LineString', coordinates: newCoordinates },
    }));
  };

  return (
    <MapSVGOverlay>
      {/* CROSSWALK GRAPHICS */}
      <g>
        {selectedCrosswalk && (
          <CrosswalkComponent
            coordinates={location?.geometry?.coordinates}
            crosswalk={selectedCrosswalk}
            selected
          />
        )}
        {otherCrosswalks.map((crosswalk) => {
          return (
            <CrosswalkComponent
              coordinates={location?.geometry?.coordinates}
              key={crosswalk.id}
              connectable={false}
              crosswalk={crosswalk as Partial<Crosswalk>}
              onClickCrosswalk={() => {
                setSelectedCrosswalk(crosswalk);
              }}
              onClickConnect={() => null}
            />
          );
        })}
      </g>
      {/* DRAG ELEMENT */}
      <g>
        {selectedCrosswalk?.geometry?.coordinates.map((point: Coordinates, pointIndex: number) => (
          <DragElement
            key={pointIndex}
            deleteable={true}
            centerLatLng={[...point] as [number, number]}
            setCenterLatLng={(newCoordinates: [number, number]) => {
              // if newCoordinates is not null, move this point
              if (newCoordinates) {
                handlePointUpdate(newCoordinates, pointIndex);
                // if newCoordinates is null, delete the point
              } else {
                handlePointDeletion(pointIndex);
              }
            }}
          />
        ))}
      </g>
    </MapSVGOverlay>
  );
};
